import { memo, useCallback } from "react";
import { Badge } from "@/components/base/badges/badges";
import { UnavailableOption, type UnavailableReason } from "@/components/filters/shared";
import { cx } from "@/utils/cx";

type Props = {
  value: string[];
  options: string[];
  onChange: (val: string[]) => void;
  getLabel?: (val: string) => string;
  getUnavailableReason?: UnavailableReason;
  disabled?: boolean;
};

// Memoized individual pill button to prevent re-rendering all pills when one is toggled
const PillButton = memo(
  ({
    option,
    isSelected,
    onToggle,
    getLabel,
    unavailableReason,
    disabled,
  }: {
    option: string;
    isSelected: boolean;
    onToggle: (option: string) => void;
    getLabel?: (val: string) => string;
    unavailableReason?: string;
    disabled: boolean;
  }) => {
    const handleClick = useCallback(() => onToggle(option), [onToggle, option]);
    const label = <span className="text-sm">{getLabel?.(option) ?? option}</span>;

    if (unavailableReason && !isSelected) {
      return (
        <UnavailableOption reason={unavailableReason} className="inline-flex opacity-50">
          <Badge type="pill-color" size="md" color="gray">
            {label}
          </Badge>
        </UnavailableOption>
      );
    }

    return (
      <button
        type="button"
        disabled={disabled}
        onClick={handleClick}
        className={disabled ? "cursor-not-allowed" : undefined}
      >
        <Badge type="pill-color" size="md" color={isSelected ? "black" : "gray"}>
          {label}
        </Badge>
      </button>
    );
  },
);

PillButton.displayName = "PillButton";

export const PillField = memo(
  ({ value, options, onChange, getLabel, getUnavailableReason, disabled = false }: Props) => {
    // Memoize the toggle handler to prevent recreation on each render
    const handleToggle = useCallback(
      (option: string) => {
        onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option]);
      },
      [onChange, value],
    );

    return (
      <div className={cx("flex flex-row flex-wrap gap-1", disabled && "opacity-50 pointer-events-none")}>
        {options?.map((option) => (
          <PillButton
            key={option}
            option={option}
            isSelected={value.includes(option)}
            onToggle={handleToggle}
            getLabel={getLabel}
            unavailableReason={getUnavailableReason?.(option)}
            disabled={disabled}
          />
        ))}
      </div>
    );
  },
);

PillField.displayName = "PillField";
